import { useMemo, useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Card } from '../components/primitives/Card'
import { PageHead } from '../components/primitives/PageHead'
import { CalcPanel } from '../components/primitives/CalcPanel'
import { DateRangeFilter } from '../components/primitives/DateRangeFilter'
import { HrAccessGuard } from '../components/hr/HrAccessGuard'
import { downloadPayrollCsv, type PayrollExportResult } from '../api/payroll'

interface FormState {
  from: string
  to: string
}

function startOfPreviousMonth(today: Date): { from: string; to: string } {
  // Default the picker to "last calendar month" — that's the typical
  // payroll cycle, so HR's most common click lands on the right window.
  const firstOfThisMonth = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastOfPrevMonth = new Date(firstOfThisMonth.getTime() - 86_400_000)
  const firstOfPrevMonth = new Date(
    lastOfPrevMonth.getFullYear(),
    lastOfPrevMonth.getMonth(),
    1,
  )
  const iso = (d: Date): string => d.toISOString().slice(0, 10)
  return { from: iso(firstOfPrevMonth), to: iso(lastOfPrevMonth) }
}

/** Approximate the number of calendar days in the selected range. */
function dayCount(from: string, to: string): number {
  if (!from || !to || from > to) return 0
  const diff = new Date(to).getTime() - new Date(from).getTime()
  return Math.round(diff / 86_400_000) + 1
}

export function HrPayrollPage(): JSX.Element {
  const defaults = useMemo(() => startOfPreviousMonth(new Date()), [])
  const [form, setForm] = useState<FormState>(defaults)
  const [lastExport, setLastExport] = useState<PayrollExportResult | null>(null)
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => downloadPayrollCsv({ from: form.from, to: form.to }),
    onSuccess: (result) => {
      setLastExport(result)
      setError(null)
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Export failed')
    },
  })

  const valid = form.from !== '' && form.to !== '' && form.from <= form.to
  const days = dayCount(form.from, form.to)

  return (
    <HrAccessGuard>
      <PageHead
        eyebrow="HR"
        title="Payroll Export"
        subtitle="Download approved leaves as CSV for the payroll cycle. Each export is audit-logged."
        actions={
          <button
            type="button"
            data-testid="payroll-download"
            onClick={() => mutation.mutate()}
            disabled={!valid || mutation.isPending}
            style={{
              padding: '9px 20px',
              borderRadius: 8,
              border: 'none',
              background: 'var(--steel-blue)',
              color: 'var(--surface)',
              fontWeight: 600,
              fontSize: 14,
              cursor: !valid || mutation.isPending ? 'not-allowed' : 'pointer',
              opacity: !valid || mutation.isPending ? 0.6 : 1,
              minHeight: 44,
            }}
          >
            {mutation.isPending ? 'Exporting…' : 'Download CSV'}
          </button>
        }
      />

      {/* T3 FormWithPreview: form on left, preview on right */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 16,
          maxWidth: 900,
          alignItems: 'start',
        }}
      >
        {/* ─── Left: Form ──────────────────────────────────────────────── */}
        <Card padding={24}>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 9,
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--text-3)',
              marginBottom: 12,
            }}
          >
            Period
          </div>

          <DateRangeFilter
            from={form.from}
            to={form.to}
            onChange={(f, t) => setForm({ from: f, to: t })}
          />

          {error ? (
            <div
              role="alert"
              style={{
                marginTop: 16,
                padding: '10px 12px',
                borderRadius: 8,
                background: 'var(--danger-bg)',
                color: 'var(--danger-fg)',
                border: '1px solid var(--danger-line)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          ) : null}

          {lastExport ? (
            <div
              data-testid="payroll-last-export"
              style={{
                marginTop: 16,
                padding: '10px 12px',
                borderRadius: 8,
                background: 'var(--success-bg)',
                color: 'var(--success-fg)',
                border: '1px solid var(--success-line)',
                fontSize: 13,
              }}
            >
              Exported <strong>{lastExport.rows}</strong>{' '}
              row{lastExport.rows === 1 ? '' : 's'} →{' '}
              <code style={{ fontSize: 12 }}>{lastExport.filename}</code>
            </div>
          ) : null}

          <div
            style={{
              marginTop: 20,
              paddingTop: 16,
              borderTop: '1px solid var(--french-gray-2)',
              fontSize: 12,
              color: 'var(--text-3)',
              lineHeight: 1.5,
            }}
          >
            <p style={{ margin: '0 0 6px 0', fontWeight: 600, color: 'var(--text-2)' }}>
              What's in the file
            </p>
            One row per approved leave with <code>start_date</code> in the
            window. Columns:{' '}
            <code>employee_id</code>, <code>employee_name</code>,{' '}
            <code>employee_email</code>, <code>leave_type</code>,{' '}
            <code>days</code> (working days, half-day reports{' '}
            <code>0.5</code>), <code>half_day_flag</code>,{' '}
            <code>start_date</code>, <code>end_date</code>,{' '}
            <code>approved_at</code>, <code>decided_by</code>.
          </div>
        </Card>

        {/* ─── Right: CalcPanel preview (Calculated register — no indigo) ── */}
        <CalcPanel
          title="Export preview"
          sub="based on selected period"
          meta="deterministic"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

            <PreviewStat
              label="Period"
              value={valid ? `${form.from} – ${form.to}` : '—'}
              sub={valid ? `${days} calendar day${days === 1 ? '' : 's'}` : 'Select a range'}
            />

            <PreviewStat
              label="Rows in export"
              value={lastExport != null ? String(lastExport.rows) : '—'}
              sub={lastExport != null ? `Last exported to ${lastExport.filename}` : 'Not yet exported'}
            />

            <PreviewStat
              label="Audit log"
              value="On"
              sub="Each download creates an audit entry"
            />

            <PreviewStat
              label="Format"
              value="CSV"
              sub="UTF-8, comma-separated, one header row"
            />
          </div>
        </CalcPanel>
      </div>
    </HrAccessGuard>
  )
}

// ─── Preview stat cell ────────────────────────────────────────────────────────

interface PreviewStatProps {
  label: string
  value: string
  sub?: string
}

function PreviewStat({ label, value, sub }: PreviewStatProps): JSX.Element {
  return (
    <div
      style={{
        borderBottom: '1px solid var(--french-gray-2)',
        paddingBottom: 12,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: 'var(--text-3)',
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: 'var(--text)',
          fontFamily: value === '—' ? undefined : 'var(--font-mono)',
          letterSpacing: value === '—' ? undefined : '-0.01em',
        }}
      >
        {value}
      </div>
      {sub != null && (
        <div style={{ marginTop: 2, fontSize: 11, color: 'var(--text-3)' }}>
          {sub}
        </div>
      )}
    </div>
  )
}
